<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import axios from "@/plugins/axios.js";
import { useStore } from "vuex";

const route = useRoute();
const router = useRouter();
const store = useStore();

const idxLeSiteMain = store.getters.getIdxLeSiteMain;
const idxLeSugangPayInfo = route.query.idxLeSugangPayInfo;

const paymentInfo = ref({});
const uploadedFile = ref("");
const isLoading = ref(false);
const payment = ref(null);
const orderId = ref("");

const getClientKey = () => {
  const href = location.href;
  if (href.includes("localhost") || href.includes("local") || href.includes("stage") || href.includes("192.168.")) {
    return "test_ck_AQ92ymxN34NNOOBevbGgrajRKXvd";
  }
  return "live_ck_6BYq7GWPVv2qD0jeOkeGVNE5vbo1";
};

const clientKey = getClientKey();
const customerKey = "cust_" + Math.random().toString(36).slice(2);

const classScheduleText = computed(() => {
  if (!paymentInfo.value) return "";

  const { sugangClassMonth, sugangNumberOfClass, sugangLessonTime, lecDayString } = paymentInfo.value;

  if (!sugangNumberOfClass || !sugangLessonTime) return "";

  if (sugangClassMonth === 0) {
    return `주 ${sugangNumberOfClass}회 ${sugangLessonTime}분 (${lecDayString || ""})`;
  }

  return `${sugangClassMonth}개월 주 ${sugangNumberOfClass}회 ${sugangLessonTime}분 (${lecDayString || ""})`;
});

const classFrequencyText = computed(() => {
  if (!paymentInfo.value) return "";

  const { sugangNumberOfClass, sugangLessonTime, lecDayString } = paymentInfo.value;

  if (!sugangNumberOfClass || !sugangLessonTime) return "";

  return `주 ${sugangNumberOfClass}회 ${sugangLessonTime}분 (${lecDayString || ""})`;
});

const formatPhoneNumber = (value) => {
  const numbers = value.replace(/[^\d]/g, "");
  if (numbers.length <= 3) return numbers;
  if (numbers.length <= 7) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
  return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
};

const formatMobile = (e) => {
  paymentInfo.value.memberMobile = formatPhoneNumber(e.target.value);
};

const formatBookMobile = (e) => {
  paymentInfo.value.bookMemberMobile = formatPhoneNumber(e.target.value);
};

const getRegisterDetail = async () => {
  try {
    const res = await axios.get("/user/sugang/class/registerDetail", {
      params: { idxLeSugangPayInfo },
    });

    if (res.data.success) {
      paymentInfo.value = res.data.data;
    } else {
      alert(res.data.message);
      router.push({ path: "/home" });
    }
  } catch (err) {
    console.error(err);
    alert("신청 내역을 불러오는데 실패했습니다.");
  }
};

const execDaumPostcode = (type) => {
  new window.daum.Postcode({
    oncomplete: (data) => {
      const buildingName = data.buildingName ? ` (${data.buildingName})` : "";
      if (type === "member") {
        paymentInfo.value.memberPostCode = data.zonecode;
        paymentInfo.value.memberAddress = data.roadAddress + buildingName;
      } else if (type === "book") {
        paymentInfo.value.bookMemberPostCode = data.zonecode;
        paymentInfo.value.bookMemberAddress = data.roadAddress + buildingName;
      }
    },
  }).open();
};

const initializePayment = () => {
  console.log("[결제] Toss 결제 모듈 스크립트 로드 시작");
  const script = document.createElement("script");
  script.src = "https://js.tosspayments.com/v2/standard";
  script.onload = () => {
    const tossPayments = window.TossPayments(clientKey);
    payment.value = tossPayments.payment({ customerKey });
    console.log("[결제] Toss 결제 모듈 초기화 완료");
  };
  script.onerror = () => {
    console.error("[결제] Toss 결제 모듈 스크립트 로드 실패");
  };
  document.head.appendChild(script);
};

const validateInputs = () => {
  // 회원 정보(memberName/memberEname/memberMobile/memberPostCode/memberAddress/memberAddressSub)는
  // 검증하지 않는다 — 회원정보 섹션이 제거돼 이 페이지에 입력란이 없고(95b76e0), 값은 API 응답에서만
  // 오므로 le_sugang_pay_info 행에 회원 주소가 비어 있으면 배송지를 다 채워도 alert이 떠 결제가 막혔다.
  // 백엔드도 이 필드들을 필수로 보지 않는다(pay/check는 미검증, 저장 SQL은 memberName 있을 때만 반영).
  if (!paymentInfo.value.bookMemberName?.trim()) return alert("교재 수령인을 입력해주세요."), false;
  if (!paymentInfo.value.bookMemberMobile) return alert("배송지 휴대전화 번호를 입력해주세요."), false;
  if (!paymentInfo.value.bookMemberPostCode) return alert("배송지 우편번호를 입력해주세요."), false;
  if (!paymentInfo.value.bookMemberAddress) return alert("배송지 주소를 입력해주세요."), false;
  if (!paymentInfo.value.bookMemberAddressSub?.trim()) return alert("배송지 상세주소를 입력해주세요."), false;
  return true;
};

const requestPay = async () => {
  console.log("[결제] 결제 요청 시작 (requestPay)");
  const isValid = validateInputs();
  if (!isValid || isLoading.value) {
    console.log("[결제] 입력값 검증 실패 또는 이미 처리 중 - 중단", {
      validateFailed: !isValid,
      isLoading: isLoading.value,
    });
    return;
  }
  if (!payment.value) {
    console.warn("[결제] 결제 모듈 미초기화 상태 - 중단");
    return alert("결제 모듈이 초기화되지 않았습니다.");
  }

  isLoading.value = true;

  const memberMobile = paymentInfo.value.memberMobile;
  const bookMemberMobile = paymentInfo.value.bookMemberMobile;
  const memberMobileForToss = memberMobile.replace(/[^\d]/g, "");

  try {
    console.log("[결제] pay/check 요청 - 결제정보 사전 검증", {
      idxLeSugangPayInfo,
      totalPay: paymentInfo.value.totalPay,
    });
    const checkRes = await axios.post("/user/sugang/pay/check", {
      idxLeSugangPayInfo,
      totalPay: paymentInfo.value.totalPay,
      payWay: "C",
      memberName: paymentInfo.value.memberName,
      memberEname: paymentInfo.value.memberEname,
      memberMobile,
      memberPostCode: paymentInfo.value.memberPostCode,
      memberAddress: paymentInfo.value.memberAddress,
      memberAddressSub: paymentInfo.value.memberAddressSub,
      bookMemberName: paymentInfo.value.bookMemberName,
      bookMemberMobile,
      bookMemberPostCode: paymentInfo.value.bookMemberPostCode,
      bookMemberAddress: paymentInfo.value.bookMemberAddress,
      bookMemberAddressSub: paymentInfo.value.bookMemberAddressSub,
    });

    if (!checkRes.data.success) {
      console.warn("[결제] pay/check 실패:", checkRes.data.message);
      // 이미 결제 완료된 건이면 재시도 화면에 묶어두지 말고 결과(완료) 화면으로 보낸다.
      if (checkRes.data.message?.includes("이미 결제")) {
        alert("이미 결제가 완료된 신청입니다.");
        const isMobile = window.innerWidth < 1200;
        router.push({
          path: isMobile ? "/enrolment/classOkMobile" : "/enrolment/classOk",
          query: { idxLeSugangPayInfo },
        });
        return;
      }
      alert(checkRes.data.message);
      isLoading.value = false;
      return;
    }

    orderId.value = checkRes.data.data.orderId;
    console.log("[결제] pay/check 성공 - orderId 발급 완료", { orderId: orderId.value });

    sessionStorage.setItem(
      "lifeeduPaymentInfo",
      JSON.stringify({
        ...paymentInfo.value,
        idxLeSugangPayInfo, // 결제 성공 콜백에서 URL 쿼리가 유실돼도 복구할 수 있도록 명시 저장
        orderId: orderId.value,
        memberMobile,
        bookMemberMobile,
        supDocument: uploadedFile.value,
      })
    );

    const isMobile = window.innerWidth < 1200;
    console.log("[결제] 결제 세션 저장 완료, 디바이스 분기", { isMobile });

    const successUrl = isMobile
      ? `${window.location.origin}/enrolment/classOkMobile?idxLeSugangPayInfo=${idxLeSugangPayInfo}&idxLeSiteMain=${idxLeSiteMain}`
      : `${window.location.origin}/enrolment/classOk?idxLeSugangPayInfo=${idxLeSugangPayInfo}&idxLeSiteMain=${idxLeSiteMain}`;

    // 과세 면세 확인
    const params = {
      idxLeSiteMain,
    };

    const res = await axios.get(`/user/page/siteMain`, { params });
    const isTax = res.data.data.isTax === "Y";
    console.log(`과세여부: ${isTax}`);

    console.log("[결제] Toss requestPayment 호출 - 카드 결제창 오픈 시도", {
      orderId: orderId.value,
      amount: paymentInfo.value.totalPay,
      method: "CARD",
      isTax,
    });
    await payment.value.requestPayment({
      method: "CARD",
      amount: { currency: "KRW", value: paymentInfo.value.totalPay },
      orderId: orderId.value,
      orderName: `${paymentInfo.value.sugangAdult}/${paymentInfo.value.sugangLang}`,
      successUrl,
      failUrl: `${window.location.origin}/enrolment/checkDetail?idxLeSugangPayInfo=${idxLeSugangPayInfo}&idxLeSiteMain=${idxLeSiteMain}`,
      customerName: paymentInfo.value.memberName,
      customerEmail: paymentInfo.value.memberEmail,
      customerMobilePhone: memberMobileForToss,
      card: { useEscrow: false },
      taxFreeAmount: isTax ? 0 : paymentInfo.value.totalPay,
    });
    console.log("[결제] requestPayment 완료 - 결제창으로 이동");
  } catch (err) {
    console.error("[결제] requestPay 처리 중 에러:", err?.code || "", err?.message || err);
    console.error(err);
    alert("결제 요청에 실패했습니다.");
    isLoading.value = false;
  }
};

const confirmPayZero = async () => {
  if (!validateInputs()) return;

  try {
    const params = {
      idxLeSugangPayInfo,
      memberEmail: paymentInfo.value.memberEmail,
      merchantUid: orderId.value || null,
      supDocument: uploadedFile.value,
      totalPay: paymentInfo.value.totalPay,
      memberName: paymentInfo.value.memberName,
      memberEname: paymentInfo.value.memberEname,
      memberMobile: paymentInfo.value.memberMobile,
      memberPostCode: paymentInfo.value.memberPostCode,
      memberAddress: paymentInfo.value.memberAddress,
      memberAddressSub: paymentInfo.value.memberAddressSub,
      bookMemberName: paymentInfo.value.bookMemberName,
      bookMemberMobile: paymentInfo.value.bookMemberMobile,
      bookMemberPostCode: paymentInfo.value.bookMemberPostCode,
      bookMemberAddress: paymentInfo.value.bookMemberAddress,
      bookMemberAddressSub: paymentInfo.value.bookMemberAddressSub,
    };

    const res = await axios.post("/user/sugang/pay/confirmPayZero", params);

    if (!res.data.success) {
      alert(res.data.message);
      return;
    }

    if (paymentInfo.value.waitStatus === "Y") {
      alert(`대기 신청이 완료되었습니다.\n신청 확정 시 담당 매니저가\n개별 안내드릴 예정입니다.`);
    }

    const isMobile = window.innerWidth < 1200;

    router.push({
      path: isMobile ? "/enrolment/classOkMobile" : "/enrolment/classOk",
      query: { idxLeSugangPayInfo },
    });
  } catch (err) {
    console.error(err);
    alert("신청 처리 중 오류가 발생했습니다.");
  }
};

onMounted(() => {
  getRegisterDetail();
  initializePayment();
});
</script>

<template>
  <section class="pc-con-wrapper1 pb-[70px] pt-[55px] pc:py-[140px]">
    <div>
      <p class="text-xl font-bold text-center mb-7 pc:mb-11 pc:text-4xl">
        <span v-if="paymentInfo.waitStatus === 'Y'">대기</span>신청내역 확인
      </p>

      <ul class="flex flex-col mb-14 gap-14 pc:mb-24 pc:gap-36">
        

        <!-- 수강 정보 -->
        <li>
          <p class="mb-5 text-lg font-bold pc:mb-4 pc:text-2xl">수강 정보</p>
          <ul class="grid grid-cols-1 border-t border-b border-solid divide-y divide-solid divide-neutral-200 border-neutral-950">
            <!-- 수업 스케줄 -->
            <li class="flex items-center">
              <div
                class="flex w-24 flex-none items-center justify-center self-stretch bg-neutral-50 px-2.5 py-3 pc:w-60 pc:px-9 pc:py-4"
              >
                <p class="text-sm font-bold text-center text-neutral-950 pc:text-lg">수업 스케줄</p>
              </div>
              <div class="grow py-3 pl-2.5 pc:py-4 pc:pl-9">
                <p class="text-sm font-medium text-neutral-500 pc:text-lg">
                  {{ paymentInfo.sugangAdult }} /
                  {{ paymentInfo.sugangLecType === "1:1" ? paymentInfo.sugangLecType + " 수업" : paymentInfo.sugangType }}
                </p>
              </div>
            </li>

            <!-- 1:1 수업 유형 -->
            <li v-if="paymentInfo.sugangLecType === '1:1'" class="flex items-center">
              <div
                class="flex w-24 flex-none items-center justify-center self-stretch bg-neutral-50 px-2.5 py-3 pc:w-60 pc:px-9 pc:py-4"
              >
                <p class="text-sm font-bold text-center text-neutral-950 pc:text-lg">수업 유형</p>
              </div>
              <div class="grow py-3 pl-2.5 pc:py-4 pc:pl-9">
                <p class="text-sm font-medium text-neutral-500 pc:text-lg">{{ paymentInfo.sugangType }}</p>
              </div>
            </li>

            <!-- 그룹 -->
            <li v-else class="flex items-center">
              <div
                class="flex w-24 flex-none items-center justify-center self-stretch bg-neutral-50 px-2.5 py-3 pc:w-60 pc:px-9 pc:py-4"
              >
                <p class="text-sm font-bold text-center text-neutral-950 pc:text-lg">그룹</p>
              </div>
              <div class="grow py-3 pl-2.5 pc:py-4 pc:pl-9">
                <p class="text-sm font-medium text-neutral-500 pc:text-lg">{{ paymentInfo.sugangLecType }}</p>
              </div>
            </li>

            <!-- 언어 -->
            <li class="flex items-center">
              <div
                class="flex w-24 flex-none items-center justify-center self-stretch bg-neutral-50 px-2.5 py-3 pc:w-60 pc:px-9 pc:py-4"
              >
                <p class="text-sm font-bold text-center text-neutral-950 pc:text-lg">언어</p>
              </div>
              <div class="grow py-3 pl-2.5 pc:py-4 pc:pl-9">
                <p class="text-sm font-medium text-neutral-500 pc:text-lg">{{ paymentInfo.sugangLang }}</p>
              </div>
            </li>

            <!-- 수업 기간/횟수 -->
            <li class="flex items-center">
              <div
                class="flex w-24 flex-none items-center justify-center self-stretch bg-neutral-50 px-2.5 py-3 pc:w-60 pc:px-9 pc:py-4"
              >
                <p class="text-sm font-bold text-center text-neutral-950 pc:text-lg">수업 기간/횟수</p>
              </div>
              <div class="grow py-3 pl-2.5 pc:py-4 pc:pl-9">
                <p class="text-sm font-medium text-neutral-500 pc:text-lg">{{ classScheduleText }}</p>
              </div>
            </li>

            <!-- 레벨 -->
            <li class="flex items-center">
              <div
                class="flex w-24 flex-none items-center justify-center self-stretch bg-neutral-50 px-2.5 py-3 pc:w-60 pc:px-9 pc:py-4"
              >
                <p class="text-sm font-bold text-center text-neutral-950 pc:text-lg">레벨</p>
              </div>
              <div class="grow py-3 pl-2.5 pc:py-4 pc:pl-9">
                <p class="text-sm font-medium text-neutral-500 pc:text-lg">{{ paymentInfo.sugangLevel }}</p>
              </div>
            </li>

            <!-- 수업 -->
            <li class="flex items-center">
              <div
                class="flex w-24 flex-none items-center justify-center self-stretch bg-neutral-50 px-2.5 py-3 pc:w-60 pc:px-9 pc:py-4"
              >
                <p class="text-sm font-bold text-center text-neutral-950 pc:text-lg">수업</p>
              </div>
              <div class="grow py-3 pl-2.5 pc:py-4 pc:pl-9">
                <p class="text-sm font-medium text-neutral-500 pc:text-lg">{{ paymentInfo.sugangClassName }}</p>
              </div>
            </li>

            <!-- 1:1 수업 횟수 -->
            <li v-if="paymentInfo.sugangLecType === '1:1'" class="flex items-center">
              <div
                class="flex w-24 flex-none items-center justify-center self-stretch bg-neutral-50 px-2.5 py-3 pc:w-60 pc:px-9 pc:py-4"
              >
                <p class="text-sm font-bold text-center text-neutral-950 pc:text-lg">수업 횟수</p>
              </div>
              <div class="grow py-3 pl-2.5 pc:py-4 pc:pl-9">
                <p class="text-sm font-medium text-neutral-500 pc:text-lg">{{ classFrequencyText }}</p>
              </div>
            </li>

            <!-- 그룹 수업시작시간 -->
            <li v-else class="flex items-center">
              <div
                class="flex w-24 flex-none items-center justify-center self-stretch bg-neutral-50 px-2.5 py-3 pc:w-60 pc:px-9 pc:py-4"
              >
                <p class="text-sm font-bold text-center text-neutral-950 pc:text-lg">수업시작시간</p>
              </div>
              <div class="grow py-3 pl-2.5 pc:py-4 pc:pl-9">
                <p class="text-sm font-medium text-neutral-500 pc:text-lg">{{ paymentInfo.sugangTime }}</p>
              </div>
            </li>

            <!-- 1:1 희망 수업시간 -->
            <li v-if="paymentInfo.sugangLecType === '1:1'" class="flex items-center">
              <div
                class="flex w-24 flex-none items-center justify-center self-stretch bg-neutral-50 px-2.5 py-3 pc:w-60 pc:px-9 pc:py-4"
              >
                <p class="text-sm font-bold text-center text-neutral-950 pc:text-lg">희망 수업시간</p>
              </div>
              <div class="grow py-3 pl-2.5 pc:py-4 pc:pl-9">
                <p class="text-sm font-medium text-neutral-500 pc:text-lg">
                  {{ paymentInfo.sugangHopeTime1 }}, {{ paymentInfo.sugangHopeTime2 }}
                </p>
              </div>
            </li>

            <!-- 교육비 -->
            <li class="flex items-center">
              <div
                class="flex w-24 flex-none items-center justify-center self-stretch bg-neutral-50 px-2.5 py-3 pc:w-60 pc:px-9 pc:py-4"
              >
                <p class="text-sm font-bold text-center text-neutral-950 pc:text-lg">교육비</p>
              </div>
              <div class="grow py-3 pl-2.5 pc:py-4 pc:pl-9">
                <p class="text-sm font-medium text-neutral-500 pc:text-lg">{{ paymentInfo.classPay }}원</p>
              </div>
            </li>

            <!-- 교재비 -->
            <li class="flex items-center">
              <div
                class="flex w-24 flex-none items-center justify-center self-stretch bg-neutral-50 px-2.5 py-3 pc:w-60 pc:px-9 pc:py-4"
              >
                <p class="text-sm font-bold text-center text-neutral-950 pc:text-lg">교재비</p>
              </div>
              <div class="grow py-3 pl-2.5 pc:py-4 pc:pl-9">
                <div class="flex flex-wrap items-center gap-x-4 gap-y-1">
                  <p class="text-sm font-medium leading-5 text-neutral-500 pc:text-lg">
                    {{ paymentInfo.isBookBuy === "N" ? 0 : paymentInfo.bookPay }}원
                  </p>

                  <div v-if="route.query.isRenew === 'Y'" class="text-xs leading-5 text-neutral-500 pc:text-base">
                    ※ 재수강 신청 시 교재는 자동으로 구매되지 않으며, 교재비는 0원으로 적용됩니다.<br />
                    ※ 교재가 필요하신 경우 반드시 당근영어로 연락 바랍니다.
                  </div>
                </div>
              </div>
            </li>

            <!-- 총 결제금액 -->
            <li class="flex items-center">
              <div
                class="flex w-24 flex-none items-center justify-center self-stretch bg-neutral-50 px-2.5 py-3 pc:w-60 pc:px-9 pc:py-4"
              >
                <p class="text-sm font-bold text-center text-neutral-950 pc:text-lg">총 결제금액</p>
              </div>
              <div class="grow py-3 pl-2.5 pc:py-4 pc:pl-9">
                <p class="text-sm font-medium text-neutral-500 pc:text-lg">{{ paymentInfo.totalPay }}원</p>
              </div>
            </li>
          </ul>
        </li>

        <!-- 교재 배송지 정보 -->
        <li>
          <p class="mb-5 text-lg font-bold pc:mb-4 pc:text-2xl">교재 배송지 정보</p>
          <ul class="grid grid-cols-1 border-t border-b border-solid divide-y divide-solid divide-neutral-200 border-neutral-950">
            <!-- 받는사람 -->
            <li class="flex items-center">
              <div
                class="flex w-24 flex-none items-center justify-center self-stretch bg-neutral-50 px-2.5 py-3 pc:w-60 pc:px-9 pc:py-4"
              >
                <p class="text-sm font-bold text-center text-neutral-950 pc:text-lg">받는사람</p>
              </div>
              <div class="grow py-3 pl-2.5 pc:py-4 pc:pl-9">
                <input
                  type="text"
                  placeholder="받는사람"
                  class="p-4 text-xs border border-solid rounded border-neutral-200 text-neutral-500 placeholder:text-neutral-200 pc:w-80 pc:text-base"
                  v-model="paymentInfo.bookMemberName"
                />
              </div>
            </li>

            <!-- 휴대전화 -->
            <li class="flex items-center">
              <div
                class="flex w-24 flex-none items-center justify-center self-stretch bg-neutral-50 px-2.5 py-3 pc:w-60 pc:px-9 pc:py-4"
              >
                <p class="text-sm font-bold text-center text-neutral-950 pc:text-lg">휴대전화</p>
              </div>
              <div class="grow py-3 pl-2.5 pc:py-4 pc:pl-9">
                <input
                  type="tel"
                  placeholder="010-1234-5678"
                  class="p-4 text-xs border border-solid rounded border-neutral-200 text-neutral-500 placeholder:text-neutral-200 pc:w-80 pc:text-base"
                  v-model="paymentInfo.bookMemberMobile"
                  @input="formatBookMobile"
                  maxlength="13"
                />
              </div>
            </li>

            <!-- 주소 -->
            <li class="flex items-center">
              <div
                class="flex w-24 flex-none items-center justify-center self-stretch bg-neutral-50 px-2.5 py-3 pc:w-60 pc:px-9 pc:py-4"
              >
                <p class="text-sm font-bold text-center text-neutral-950 pc:text-lg">주소</p>
              </div>
              <div class="grow py-3 pl-2.5 pc:py-4 pc:pl-9">
                <ul class="flex flex-col gap-2">
                  <li class="flex gap-2">
                    <input
                      type="text"
                      class="p-4 text-xs border border-solid rounded border-neutral-200 pc:w-80 pc:text-base"
                      v-model="paymentInfo.bookMemberPostCode"
                      readonly
                      @click="execDaumPostcode('book')"
                      placeholder="우편번호"
                    />
                    <p
                      class="flex-none p-4 text-xs border border-solid rounded cursor-pointer border-neutral-200 bg-neutral-50 text-neutral-500 pc:text-base"
                      @click="execDaumPostcode('book')"
                    >
                      우편번호 찾기
                    </p>
                  </li>
                  <li>
                    <input
                      type="text"
                      class="p-4 text-xs border border-solid rounded border-neutral-200 pc:w-80 pc:text-base"
                      v-model="paymentInfo.bookMemberAddress"
                      readonly
                      @click="execDaumPostcode('book')"
                      placeholder="주소"
                    />
                  </li>
                  <li>
                    <input
                      type="text"
                      class="p-4 text-xs border border-solid rounded border-neutral-200 pc:w-80 pc:text-base"
                      v-model="paymentInfo.bookMemberAddressSub"
                      placeholder="상세주소"
                    />
                  </li>
                </ul>
              </div>
            </li>
          </ul>
        </li>
      </ul>

      <!-- 결제 버튼 -->
      <ul v-if="paymentInfo.totalPay" class="mx-auto grid max-w-xs grid-cols-1 gap-2.5 pc:max-w-4xl pc:grid-cols-1 pc:gap-5">
        <li
          v-if="paymentInfo.waitStatus === 'Y'"
          class="py-4 text-sm font-bold text-center text-orange-500 border border-orange-500 border-solid rounded-sm cursor-pointer pc:rounded pc:py-6 pc:text-xl"
          @click="confirmPayZero"
        >
          대기 신청 하기
        </li>
        <li
          v-else
          class="py-4 text-sm font-bold text-center text-orange-500 transition-opacity border border-orange-500 border-solid rounded-sm cursor-pointer pc:rounded pc:py-6 pc:text-xl"
          :class="{ 'pointer-events-none opacity-50': isLoading }"
          @click="requestPay"
        >
          {{ isLoading ? "결제 진행중..." : "결제하기" }}
        </li>
      </ul>

      <!-- 결제 금액이 없을 경우 -->
      <p
        v-else
        class="max-w-xs py-4 mx-auto text-sm font-bold text-center text-white bg-orange-500 rounded cursor-pointer pc:py-6 pc:text-xl"
        @click="confirmPayZero"
      >
        {{ paymentInfo.waitStatus === "Y" ? "대기 신청 하기" : "확인" }}
      </p>
    </div>
  </section>
</template>
